/**
 * Multi-Provider Icon Component
 * Supports multiple icon providers with extensible architecture
 * Currently supports FontAwesome React components
 *
 * Usage:
 * <Icon name="home" /> - Uses FontAwesome solid by default
 * <Icon name="home" style="regular" />
 * <Icon name="home" size={24} color="#333" />
 * <Icon name="facebook" style="brands" /> - Brand icons
 *
 * Available icons: home, user, cart, search, heart, star, menu, close,
 * chevron-down/up/left/right, check, calendar, envelope, lock, map-marker,
 * phone, globe, cog, bell, eye, eye-slash, edit, trash, plus, minus,
 * arrow-left/right/up/down, info, exclamation, question, check-circle,
 * exclamation-circle, times-circle, spinner, download, upload, filter,
 * sort, sort-up, sort-down, and brand icons (facebook, twitter, instagram, etc.)
 *
 * Adding new providers:
 * ```typescript
 * import { providerRegistry } from './Icon';
 *
 * class MyIconProvider implements IconProvider {
 *   name = 'myprovider';
 *   render(iconName: string, props: IconRenderProps, style?: string) {
 *     // Custom rendering logic
 *   }
 * }
 *
 * providerRegistry.register(new MyIconProvider());
 * ```
 */
import { faApple, faFacebook, faGithub, faGoogle, faInstagram, faLinkedin, faMicrosoft, faTwitter, faWhatsapp, faYoutube } from '@fortawesome/free-brands-svg-icons';
import {
  faCheckCircle as faCheckCircleRegular,
  faCreditCard as faCreditCardRegular,
  faEye as faEyeRegular,
  faEyeSlash as faEyeSlashRegular,
  faHeart as faHeartRegular,
  faStar as faStarRegular,
  faTimesCircle as faTimesCircleRegular,
  faUser as faUserRegular,
} from '@fortawesome/free-regular-svg-icons';
import {
  IconDefinition,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowRightFromBracket,
  faArrowRightToBracket,
  faArrowUp,
  faBars,
  faBell,
  faCalendar,
  faCheck,
  faCheckCircle,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCog,
  faCreditCard,
  faDownload,
  faEdit,
  faEnvelope,
  faExclamation,
  faExclamationCircle,
  faEye,
  faEyeSlash,
  faFilter,
  faFire,
  faGlobe,
  faHeart,
  faHome,
  faInfo,
  faLock,
  faMapMarker,
  faMinus,
  faPhone,
  faPlus,
  faQuestion,
  faQuestionCircle,
  faSearch,
  faShoppingCart,
  faSort,
  faSortDown,
  faSortUp,
  faSpinner,
  faStar,
  faTag,
  faTimes,
  faTimesCircle,
  faTrash,
  faUpload,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

// Provider interface for icon systems
interface IconProvider {
  name: string;
  render: (iconName: string, props: IconRenderProps, style?: string) => React.ReactElement;
}

// Props for rendering icons
interface IconRenderProps {
  size?: number;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
}

// FontAwesome provider implementation
class FontAwesomeProvider implements IconProvider {
  name = 'fontawesome';

  private iconRegistry: { [key: string]: { [style: string]: IconDefinition } } = {
    home: { solid: faHome },
    user: { solid: faUser, regular: faUserRegular },
    cart: { solid: faShoppingCart },
    search: { solid: faSearch },
    heart: { solid: faHeart, regular: faHeartRegular },
    star: { solid: faStar, regular: faStarRegular },
    menu: { solid: faBars },
    close: { solid: faTimes },
    'chevron-down': { solid: faChevronDown },
    'chevron-up': { solid: faChevronUp },
    'chevron-left': { solid: faChevronLeft },
    'chevron-right': { solid: faChevronRight },
    check: { solid: faCheck },
    calendar: { solid: faCalendar },
    envelope: { solid: faEnvelope },
    lock: { solid: faLock },
    'map-marker': { solid: faMapMarker },
    phone: { solid: faPhone },
    globe: { solid: faGlobe },
    cog: { solid: faCog },
    card: { solid: faCreditCard, regular: faCreditCardRegular },
    bell: { solid: faBell },
    eye: { solid: faEye, regular: faEyeRegular },
    'eye-slash': { solid: faEyeSlash, regular: faEyeSlashRegular },
    edit: { solid: faEdit },
    trash: { solid: faTrash },
    plus: { solid: faPlus },
    minus: { solid: faMinus },
    'arrow-left': { solid: faArrowLeft },
    'arrow-right': { solid: faArrowRight },
    'arrow-up': { solid: faArrowUp },
    'arrow-down': { solid: faArrowDown },
    info: { solid: faInfo },
    exclamation: { solid: faExclamation },
    question: { solid: faQuestion },
    'question-circle': { solid: faQuestionCircle },
    'check-circle': { solid: faCheckCircle, regular: faCheckCircleRegular },
    'exclamation-circle': { solid: faExclamationCircle },
    tag: { solid: faTag },
    fire: { solid: faFire },
    'times-circle': { solid: faTimesCircle, regular: faTimesCircleRegular },
    spinner: { solid: faSpinner },
    download: { solid: faDownload },
    upload: { solid: faUpload },
    filter: { solid: faFilter },
    sort: { solid: faSort },
    'sort-up': { solid: faSortUp },
    'sort-down': { solid: faSortDown },
    store: { solid: faShoppingCart },
    'shopping-bag': { solid: faShoppingCart },
    logout: { solid: faArrowRightFromBracket },
    login: { solid: faArrowRightToBracket },
    box: { solid: faTag },
    bars: { solid: faBars },
    times: { solid: faTimes },
    // Add more icons as needed

    // Brand icons
    facebook: { brands: faFacebook },
    twitter: { brands: faTwitter },
    instagram: { brands: faInstagram },
    youtube: { brands: faYoutube },
    linkedin: { brands: faLinkedin },
    github: { brands: faGithub },
    google: { brands: faGoogle },
    apple: { brands: faApple },
    microsoft: { brands: faMicrosoft },
    whatsapp: { brands: faWhatsapp },
  };

  render(iconName: string, props: IconRenderProps, style: string = 'solid'): React.ReactElement {
    const { size, color, className } = props;

    // Get the icon definition
    const iconDef = this.getIconDefinition(iconName, style);
    if (!iconDef) {
      //console.warn(`FontAwesome icon "${iconName}" with style "${style}" not found`);
      return <span>?</span>;
    }

    return <FontAwesomeIcon icon={iconDef} size={this.mapSize(size)} color={color} className={className} />;
  }

  private getIconDefinition(iconName: string, style: string = 'solid'): IconDefinition | null {
    const iconEntry = this.iconRegistry[iconName];
    if (!iconEntry) return null;

    // Try requested style, fallback to solid, then any available style
    return iconEntry[style] || iconEntry.solid || Object.values(iconEntry)[0] || null;
  }

  private mapSize(size?: number): any {
    if (!size) return undefined;

    // FontAwesome size mapping
    if (size <= 12) return 'xs';
    if (size <= 16) return 'sm';
    if (size <= 20) return 'lg';
    if (size <= 32) return 'xl';
    if (size <= 48) return '2xl';
    return '3x';
  }
}

// Provider registry
class IconProviderRegistry {
  private providers: Map<string, IconProvider> = new Map();

  register(provider: IconProvider): void {
    this.providers.set(provider.name, provider);
  }

  getProvider(name: string): IconProvider | undefined {
    return this.providers.get(name);
  }

  getDefaultProvider(): IconProvider | undefined {
    return this.providers.get('fontawesome');
  }
}

// Global provider registry instance
const providerRegistry = new IconProviderRegistry();

// Register FontAwesome provider
providerRegistry.register(new FontAwesomeProvider());

// Icon component props
type IconProps = {
  name: string;
  size?: number;
  color?: string;
  style?: 'solid' | 'regular' | 'brands';
  provider?: string;
  className?: string;
  fallback?: React.ReactNode;
};

// Main Icon component
const Icon: React.FC<IconProps> = ({ name, size, color, style = 'solid', provider = 'fontawesome', className, fallback }) => {
  // Get the provider
  const iconProvider = providerRegistry.getProvider(provider) || providerRegistry.getDefaultProvider();

  if (!iconProvider) {
    //console.warn(`Icon provider "${provider}" not found. Falling back to default.`);
    return <>{fallback || <span>?</span>}</>;
  }

  try {
    return iconProvider.render(
      name,
      {
        size,
        color,
        className,
      },
      style,
    );
  } catch (error) {
    //console.warn(`Failed to render icon "${name}" with provider "${provider}":`, error);
    return <>{fallback || <span>?</span>}</>;
  }
};

// Export utilities for extending the icon system
export type { IconProvider, IconProviderRegistry };
export { providerRegistry };
export type { IconProps, IconRenderProps };

export default Icon;
